import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import turfDistance from "@turf/distance";

import Card from "./card";
import useSelectionEffects from "@/use-selection-effects";
import useAmpolRefinedServices from "@/use-refined-ampol-services";
import useIndication from "@/use-indication";
import useIndicationEffects from "@/use-indication-effects";
import useKeyPress from "@/use-key-press";
import { AmpolService } from "@/types";
import useMapEvent from "@/@alpaca-travel/react-mapbox-gl/use-map-event";
import useGtagEffects from "@/use-gtag-effects";
import useRefinementStore from "@/use-refinement-store";

const List: FunctionComponent<ListProps> = () => {
  const { ampolServices, services } = useAmpolRefinedServices();
  const position = useRefinementStore((store) => store.position);
  const [isMoving, setIsMoving] = useState(false);

  const lastCall = useRef<string>();

  const { setSelection } = useSelectionEffects();
  const { indicatedIds } = useIndication();
  const { setIndication } = useIndicationEffects();

  // Prevent showing the 'there are no matches' while the map is moving
  useMapEvent("move", () => setIsMoving(true));
  useMapEvent("moveend", () => setIsMoving(false));

  const { triggerEvent } = useGtagEffects();

  const onDownHandler = useCallback(() => {
    if (indicatedIds?.length === 0) {
      const [first] = ampolServices;
      if (first != null) {
        setIndication({ $ids: [first.$id] });
      }
    } else {
      const index = ampolServices.findIndex((s) => s.$id === indicatedIds[0]);
      const nextIndex = index + 1;
      const idAtNext = ampolServices[nextIndex]?.$id;
      if (idAtNext != null) {
        setIndication({ $ids: [idAtNext] });
      } else {
        setIndication(null);
      }
    }
  }, [indicatedIds, setIndication, ampolServices]);

  const onUpHandler = useCallback(() => {
    if (indicatedIds?.length === 0) {
      const [last] = ampolServices.slice(-1);
      if (last != null) {
        setIndication({ $ids: [last.$id] });
      }
    } else {
      const index = ampolServices.findIndex((s) => s.$id === indicatedIds[0]);
      const nextIndex = index - 1;
      const idAtNext = ampolServices[nextIndex]?.$id;
      if (idAtNext != null) {
        setIndication({ $ids: [idAtNext] });
      } else {
        setIndication(null);
      }
    }
  }, [indicatedIds, setIndication, ampolServices]);

  const onEnterHandler = useCallback(() => {
    if (indicatedIds?.length > 0) {
      setSelection(indicatedIds[0]);
    }
  }, [indicatedIds, setSelection]);

  useKeyPress("ArrowDown", "keydown", onDownHandler);
  useKeyPress("ArrowUp", "keydown", onUpHandler);
  useKeyPress("Enter", "keydown", onEnterHandler);

  const onSelectHandler = useCallback(
    ($id: AmpolService["$id"]) => {
      setSelection($id);
      setIndication({ $ids: [$id] });
    },
    [setSelection, setIndication]
  );

  const minimalSet = useMemo(() => {
    // Grab the first 5 services
    const minimal = (ampolServices ?? []).slice(0, 4);

    // Order them by the distance from the position
    return minimal.sort((a, b) => {
      if (position == null) {
        return 0;
      }

      const distanceA = turfDistance(a.position as [number, number], position, {
        units: "kilometers",
      });
      const distanceB = turfDistance(b.position as [number, number], position, {
        units: "kilometers",
      });

      return distanceA - distanceB;
    });
  }, [position, ampolServices]);

  useEffect(() => {
    // When we have search results
    if (isMoving === false && minimalSet.length > 0) {
      const fuelType = services.join(",");

      const data = {
        items: minimalSet.map((ampolService) => ({
          item_id: ampolService.$id,
          item_name: ampolService.name,
          item_category: ampolService.services?.includes("WOWDiscounts")
            ? "WOWDiscounts"
            : undefined,
        })),
        fuel_type: fuelType.length > 0 ? fuelType : "all",
      };

      if (
        lastCall.current == null ||
        lastCall.current !== JSON.stringify(data)
      ) {
        lastCall.current = JSON.stringify(data);
        triggerEvent("view_item_list", data);
      }
    }
  }, [minimalSet, triggerEvent, isMoving, services]);

  useEffect(() => {
    triggerEvent("screen_view", { screen_name: "browse" });
  }, [triggerEvent]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-1 tw-pointer-events-auto tw-p-2">
      {minimalSet &&
        minimalSet.map((service) => (
          <Card
            indicated={indicatedIds?.includes(service.$id)}
            service={service}
            onClick={() => {
              onSelectHandler(service.$id);
            }}
            key={service.$id}
            onMouseEnter={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIndication({ $ids: [service.$id] });
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIndication(null);
            }}
            onFocus={() => setIndication({ $ids: [service.$id] })}
            onBlur={() => setIndication(null)}
            extended
          />
        ))}

      {minimalSet.length === 0 && isMoving === false && (
        <div className="tw-bg-white tw-p-5">
          There are no nearby services. Please move the map to see more.
        </div>
      )}
    </div>
  );
};

export type ListProps = {};

export default React.memo(List);
